import { NgZone } from '@angular/core';
import { OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';

/** Scroll strategy for the job forms' Client Name mat-autocomplete.
 *
 *  The CDK's stock strategies each fail one of our two requirements:
 *  - reposition(): the panel keeps tracking its input even after the field
 *    scrolls out of the modal, floating over unrelated form/page content.
 *  - close(): closes on ANY scroll it hears — including the scroll that
 *    chains out of the options list itself — so a user wheel-scrolling
 *    through a long client list gets the panel yanked shut before they can
 *    pick an option.
 *
 *  This strategy closes the overlay only when something OUTSIDE it scrolls
 *  (the modal body, the page) and stays open while the user scrolls the
 *  options list. A capture-phase document listener is used because element
 *  scroll events don't bubble and the autocomplete panel is not a
 *  CdkScrollable, so the ScrollDispatcher can neither hear panel scrolls
 *  nor attribute global ones to a source. */
export class CloseOnOutsideScrollStrategy implements ScrollStrategy {
  private overlayRef: OverlayRef | null = null;

  constructor(private ngZone: NgZone) {}

  private onScroll = (event: Event) => {
    const overlay = this.overlayRef;
    if (!overlay || !overlay.hasAttached()) {
      return;
    }
    const target = event.target;
    if (target instanceof Node && overlay.overlayElement.contains(target)) {
      return; // user is scrolling the options list — keep the panel open
    }
    this.ngZone.run(() => overlay.detach());
  };

  attach(overlayRef: OverlayRef): void {
    this.overlayRef = overlayRef;
  }

  enable(): void {
    this.ngZone.runOutsideAngular(() =>
      document.addEventListener('scroll', this.onScroll, true));
  }

  disable(): void {
    document.removeEventListener('scroll', this.onScroll, true);
  }

  detach(): void {
    this.disable();
    this.overlayRef = null;
  }
}

/** Factory for MAT_AUTOCOMPLETE_SCROLL_STRATEGY providers (deps: [NgZone]). */
export function closeAutocompleteOnOutsideScrollFactory(ngZone: NgZone): () => ScrollStrategy {
  return () => new CloseOnOutsideScrollStrategy(ngZone);
}
